import { IconV1 } from 'common/icon/iconV1.js';
import { LinkFactoryOnClick } from 'common/link/link-on-click.js';
import { Tile } from 'common/tile/tile.js';
import { LinkFakeRef } from 'components-modules/condition/link-ref.js';
import { IWarningsPanelProps } from 'components/map/map-controller/warning/types.js';
import { IModule } from 'redux-state/layout/types.js';
import { IMarker } from 'types/markerCore/marker-core-type.js';
import { getMarkerWarnings } from 'types/markerTypes/markers.warning.js';
import { getFirstOfArr } from 'utilities/isType.js';
import { WarningSeverity } from 'utilities/weather-warnings/warning-severity.js';
import { PanelContentNoWarnings } from './panel-noWarnings.js';
import { WarningPanel } from './warnings-panel-item.js';

type MarkersWithWarnings = Record<'red' | 'orange' | 'yellow' | 'total', number>;

/** Get summary of warnings in markers */
const numberOfWarnings = (markers: IMarker[]): MarkersWithWarnings => {
  const newData = { red: 0, orange: 0, yellow: 0, total: 0 };
  const result = markers.reduce<MarkersWithWarnings>((acc, marker: IMarker) => {
    const warning = getMarkerWarnings(marker);
    const firstWarnLevel = getFirstOfArr(warning)?.warnLevel;
    switch (firstWarnLevel) {
      case WarningSeverity.Red:
        return { ...acc, red: acc.red + 1, total: acc.total + 1 };

      case WarningSeverity.Orange:
        return { ...acc, orange: acc.orange + 1, total: acc.total + 1 };

      case WarningSeverity.Yellow:
        return { ...acc, yellow: acc.yellow + 1, total: acc.total + 1 };

      default:
        return acc;
    }
  }, newData);
  return result;
};

/** Populate title text for panel item */
const getTitle = ({ red, orange, yellow, total }: MarkersWithWarnings) => {
  const titleSuffix = ' in force for this location';

  const totalRedOrange = red + orange;

  if (total === 0) {
    return 'No severe weather watches or warnings'.concat(titleSuffix);
  } else {
    let title = '';

    if (totalRedOrange > 0) {
      title += `${totalRedOrange} Warning${totalRedOrange !== 1 ? 's' : ''}`;
    }

    // If there's warnings and watch then add an 'and'
    if (yellow > 0 && totalRedOrange > 0) {
      title += ` and `;
    }

    if (yellow > 0) {
      title += `${yellow} Watch${yellow !== 1 ? 'es' : ''}`;
    }

    return title.concat(titleSuffix);
  }
};

/** Display warnings information on warning page. Populates map slot info on warnings page */
export const WarningsPanel = ({ config }: IModule<IWarningsPanelProps>) => {
  const { onModuleClose, markers, showCloseText = true } = config;

  if (markers) {
    // Set up the title text based on number of warnings
    const nWarnings = numberOfWarnings(markers);
    const panelItemTitle = getTitle(nWarnings);

    const redAndOrangeMarkers = markers.filter((marker) => {
      // const subject = getFirstOfArr(marker.subject);
      const valueWarning = getMarkerWarnings(marker);
      // const valueWarning = subject?.value?.warning;
      const type = getFirstOfArr(valueWarning)?.warnLevel ?? WarningSeverity.None;
      return [WarningSeverity.Red, WarningSeverity.Orange].includes(type);
    });

    const yellowMarkers = markers.filter((marker) => {
      const valueWarning = getMarkerWarnings(marker);
      return getFirstOfArr(valueWarning)?.warnLevel === WarningSeverity.Yellow;
    });

    return (
      <Tile dataModule="warnings-panel" config={config}>
        <div className="Tile-body">
          <div className="Tile-body-header">
            <h2>{panelItemTitle}</h2>

            <LinkFactoryOnClick className="u-textGrey u-linkClean" onClick={onModuleClose}>
              <IconV1 iconClass="svg--fill-inherit svg--1em" icon="exit" />
            </LinkFactoryOnClick>
          </div>

          <ul className="WarningsPanel">
            {!nWarnings.total && <PanelContentNoWarnings />}

            {redAndOrangeMarkers.map((marker, index) => {
              const valueWarning = getMarkerWarnings(marker);
              return (
                <li className="WarningsPanel-item" key={`red-orange-${index}`} data-module="warnings-panel-item">
                  <WarningPanel warnings={valueWarning} />
                </li>
              );
            })}

            {yellowMarkers.map((marker, index) => {
              const valueWarning = getMarkerWarnings(marker);
              return (
                <li key={`yellow-${index}`} className="WarningsPanel-item" data-module="warnings-panel-item">
                  <WarningPanel warnings={valueWarning} />
                </li>
              );
            })}
          </ul>
          {showCloseText && <LinkFakeRef label={'Back to warnings overview'} onClickCB={onModuleClose} />}
        </div>
      </Tile>
    );
  }
  return null;
};
